import React, { FC } from 'react'
import { Conversation, Tag, InboxUser } from '../hooks/useChatGraphQL'
import { toAbsoluteUrl } from '../../../../../_metronic/helpers'
import ChatFilters from './ChatFilters'
import { CHANNEL_BADGE_CONFIG, getChannelBadgeInfo, getChannelBadgeTooltip, resolveConversationChannel } from '../helpers/channelBadgeHelper'
import {useIntl} from 'react-intl'
import {CHAT_MESSAGES as C, CHANNEL_SMS, CHANNEL_WHATSAPP, CHANNEL_WA_SHORT, UNKNOWN_INITIAL} from '../chatMessages'

function formatConversationDate(dateStr: string): string {
    const date = new Date(dateStr)
    const now = new Date()
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const startOfYesterday = new Date(startOfToday.getTime() - 86400000)
    const startOfWeekAgo = new Date(startOfToday.getTime() - 6 * 86400000)

    if (date >= startOfToday) {
        return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
    } else if (date >= startOfYesterday) {
        return 'Yesterday'
    } else if (date >= startOfWeekAgo) {
        return date.toLocaleDateString('en-US', { weekday: 'long' })
    } else {
        return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    }
}

/** Etiqueta del badge de canal: dato puro (canal + teléfono), no texto traducible. */
const channelTitle = (channel: string, phone?: string | null) =>
    phone ? channel + ': ' + phone : channel

function formatPhone(phone: string): string {
    const digits = phone.replace(/\D/g, '')
    if (digits.length === 11 && digits.startsWith('1')) {
        const d = digits.slice(1)
        return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
    } else if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    }
    return phone
}

type Props = {
    conversations: Conversation[]
    selectedId: number | undefined
    onSelect: (c: Conversation) => void
    onNewChat: () => void
    onRefresh: () => void
    loading: boolean
    inboxMode: 'my' | 'general' | 'all'
    onSetMode: (mode: 'my' | 'general' | 'all') => void
    isAdmin: boolean
    isSupervisor: boolean
    filterWhatsapp: boolean
    onToggleWhatsapp: (active: boolean) => void
    filterSms: boolean
    onToggleSms: (active: boolean) => void

    // Search (server-side)
    searchQuery: string
    onSetSearchQuery: (val: string) => void

    // Advanced Filters Props
    filterUnread: boolean
    onToggleUnread: (val: boolean) => void
    filterDateFrom: string
    onSetDateFrom: (val: string) => void
    filterDateTo: string
    onSetDateTo: (val: string) => void
    filterTags: number[]
    onSetTags: (tags: number[]) => void
    availableTags: Tag[]

    // User filter (admin/supervisor only)
    filterUserId: number | null
    onSetFilterUserId: (id: number | null) => void
    inboxUsers: InboxUser[]
    onFetchInboxUsers: () => void

    // Org filter (superadmin only)
    isSuperAdmin: boolean
    filterOrgId: number | null
    onSetFilterOrgId: (id: number | null) => void
    orgOptions: { value: number; label: string }[]
    onFetchOrgOptions: () => void

    // Pagination
    currentPage: number
    onSetPage: (page: number) => void
    pageSize: number
    totalItems: number
}

const ConversationList: FC<Props> = ({
    conversations,
    selectedId,
    onSelect,
    onNewChat,
    onRefresh,
    loading,
    inboxMode,
    onSetMode,
    isAdmin,
    isSupervisor,
    filterWhatsapp,
    onToggleWhatsapp,
    filterSms,
    onToggleSms,
    searchQuery,
    onSetSearchQuery,
    filterUnread,
    onToggleUnread,
    filterDateFrom,
    onSetDateFrom,
    filterDateTo,
    onSetDateTo,
    filterTags,
    onSetTags,
    availableTags,
    filterUserId,
    onSetFilterUserId,
    inboxUsers,
    onFetchInboxUsers,
    isSuperAdmin,
    filterOrgId,
    onSetFilterOrgId,
    orgOptions,
    onFetchOrgOptions,
    currentPage,
    onSetPage,
    pageSize,
    totalItems
}) => {
  const intl = useIntl()
    const activeFiltersCount =
        (inboxMode !== 'my' ? 1 : 0) +
        (filterWhatsapp ? 1 : 0) +
        (filterSms ? 1 : 0) +
        (filterUnread ? 1 : 0) +
        (filterDateFrom ? 1 : 0) +
        (filterDateTo ? 1 : 0) +
        filterTags.length +
        (filterUserId ? 1 : 0) +
        (filterOrgId ? 1 : 0)

    const clearFilters = () => {
        onSetMode('my')
        onToggleUnread(false)
        onSetDateFrom('')
        onSetDateTo('')
        onSetTags([])
        onToggleWhatsapp(false)
        onToggleSms(false)
        onSetFilterUserId(null)
        onSetFilterOrgId(null)
        onSetPage(0)
    }

    const hasNextPage = (currentPage + 1) * pageSize < totalItems
    const hasPrevPage = currentPage > 0

    // Deduplicate conversations by id, preferring items with a last_message to prevent dates from disappearing
    const uniqueConversations = React.useMemo(() => {
        const map = new Map<number, typeof conversations[0]>()
        for (const c of conversations) {
            if (!map.has(c.id)) {
                map.set(c.id, c)
            } else {
                const existing = map.get(c.id)
                if (!existing?.last_message && c.last_message) {
                    map.set(c.id, c)
                } else if (existing?.last_message && c.last_message) {
                    if (new Date(c.last_message.created_at).getTime() > new Date(existing.last_message.created_at).getTime()) {
                        map.set(c.id, c)
                    }
                }
            }
        }
        return Array.from(map.values())
    }, [conversations])

    return (
        <div className='card card-flush h-100 conversation-list'>
            <div className='card-header d-flex flex-column flex-sm-row pt-7' id='kt_chat_contacts_header'>
                <div className='card-title'>
                    <h2 className='mb-0 me-3'>{intl.formatMessage(C.conversations)}</h2>
                    <button
                        className={`btn btn-icon btn-sm btn-light-primary ${loading ? 'opacity-50' : ''}`}
                        onClick={onRefresh}
                        disabled={loading}
                        title={intl.formatMessage(C.refresh)}
                    >
                        <i className={`bi bi-arrow-clockwise fs-3 ${loading ? 'spinner-border spinner-border-sm' : ''}`}></i>
                    </button>
                </div>
                <div className='card-toolbar mt-3 mt-sm-0'>
                    <button className='btn btn-xs btn-light-primary px-3 py-2' style={{ fontSize: '0.8rem' }} onClick={onNewChat}>
                        <i className='bi bi-plus-lg fs-4'></i> {intl.formatMessage(C.newChat)}
                    </button>
                    <ChatFilters
                        filterUnread={filterUnread}
                        onToggleUnread={onToggleUnread}
                        filterDateFrom={filterDateFrom}
                        onSetDateFrom={onSetDateFrom}
                        filterDateTo={filterDateTo}
                        onSetDateTo={onSetDateTo}
                        filterTags={filterTags}
                        onSetTags={onSetTags}
                        availableTags={availableTags}
                        filterWhatsapp={filterWhatsapp}
                        onToggleWhatsapp={onToggleWhatsapp}
                        filterSms={filterSms}
                        onToggleSms={onToggleSms}
                        inboxMode={inboxMode}
                        onSetMode={onSetMode}
                        isAdmin={isAdmin}
                        isSupervisor={isSupervisor}
                        filterUserId={filterUserId}
                        onSetFilterUserId={onSetFilterUserId}
                        inboxUsers={inboxUsers}
                        onFetchInboxUsers={onFetchInboxUsers}
                        isSuperAdmin={isSuperAdmin}
                        filterOrgId={filterOrgId}
                        onSetFilterOrgId={onSetFilterOrgId}
                        orgOptions={orgOptions}
                        onFetchOrgOptions={onFetchOrgOptions}
                    />
                    <div className='search-box'>
                        <i className='bi bi-search position-absolute top-50 translate-middle-y ms-3 text-muted'></i>
                        <input
                            type='text'
                            className='form-control form-control-sm form-control-solid ps-10'
                            style={{ fontSize: '0.8rem' }}
                            placeholder={intl.formatMessage(C.searchPlaceholder)}
                            value={searchQuery}
                            onChange={(e) => onSetSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {(isAdmin || isSupervisor) && (
                <div className='px-5 py-2 border-bottom'>
                    <div className='d-flex gap-1 align-items-center'>
                        <span className='text-muted fs-8 fw-bold'>{intl.formatMessage(C.mode)}</span>
                        <button
                            className={`btn btn-xs px-3 py-1 ${inboxMode === 'my' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            style={{ fontSize: '0.75rem' }}
                            onClick={() => { onSetMode('my'); onSetPage(0) }}
                            title={intl.formatMessage(isSupervisor && !isAdmin ? C.modeTeamTooltip : C.modeMineTooltip)}
                        >
                            {intl.formatMessage(isSupervisor && !isAdmin ? C.modeTeam : C.modeMine)}
                        </button>
                        <button
                            className={`btn btn-xs px-3 py-1 ${inboxMode === 'general' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            style={{ fontSize: '0.75rem' }}
                            onClick={() => { onSetMode('general'); onSetPage(0) }}
                            title={intl.formatMessage(C.modeUnassigned)}
                        >
                            {intl.formatMessage(C.modeGeneral)}
                        </button>
                        {isAdmin && (
                            <button
                                className={`btn btn-xs px-3 py-1 ${inboxMode === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                style={{ fontSize: '0.75rem' }}
                                onClick={() => { onSetMode('all'); onSetPage(0) }}
                                title={intl.formatMessage(C.modeAll)}
                            >
                                {intl.formatMessage(C.modeAllLabel)}
                            </button>
                        )}
                    </div>
                </div>
            )}

            <div className='px-7 py-2 border-bottom d-flex justify-content-between align-items-center'>
                <div></div>
                <div className='d-flex gap-2 align-items-center'>
                    <small className='text-muted fs-8'>
                        {intl.formatMessage(C.pageOf, {current: currentPage + 1, total: Math.ceil(totalItems / pageSize), count: totalItems})}
                    </small>
                    <button
                        className='btn btn-sm btn-icon btn-light-primary'
                        onClick={() => onSetPage(currentPage - 1)}
                        disabled={!hasPrevPage}
                        title={intl.formatMessage(C.prevPage)}
                    >
                        <i className='bi bi-chevron-left fs-6'></i>
                    </button>
                    <button
                        className='btn btn-sm btn-icon btn-light-primary'
                        onClick={() => onSetPage(currentPage + 1)}
                        disabled={!hasNextPage}
                        title={intl.formatMessage(C.nextPage)}
                    >
                        <i className='bi bi-chevron-right fs-6'></i>
                    </button>
                </div>
            </div>

            <div className='card-body pb-2' id='kt_chat_contacts_body' style={{ flex: 1, overflow: 'hidden' }}>
                <div
                    className='scroll-y me-n5 pe-5'
                    data-kt-scroll='true'
                    data-kt-scroll-activate='{default: false, lg: true}'
                    data-kt-scroll-max-height='auto'
                    data-kt-scroll-dependencies='#kt_header, #kt_app_header, #kt_toolbar, #kt_app_toolbar, #kt_footer, #kt_app_footer, #kt_chat_contacts_header'
                    data-kt-scroll-wrappers='#kt_content, #kt_app_content, #kt_chat_contacts_body'
                    data-kt-scroll-offset='5px'
                    style={{ height: '100%', overflowY: 'auto' }}
                >
                    {loading && (
                        <div className="d-flex justify-content-center my-4">
                            <span className="spinner-border text-primary" role="status"></span>
                        </div>
                    )}

                    {!loading && uniqueConversations.length === 0 && (
                        <div className="text-center text-muted my-10">
                            {activeFiltersCount > 0 ? (
                                <div className="d-flex flex-column align-items-center">
                                    <div className="mb-3 fs-5">{intl.formatMessage(C.noMatches)}</div>
                                    <button
                                        className="btn btn-sm btn-light-primary"
                                        onClick={clearFilters}
                                    >
                                        {intl.formatMessage(C.clearFilters)}
                                    </button>
                                </div>
                            ) : (
                                <div>{intl.formatMessage(C.noConversations)}</div>
                            )}
                        </div>
                    )}

                    {uniqueConversations.map((c) => {
                        const channelType = resolveConversationChannel(c)
                        const channelConfig = channelType ? CHANNEL_BADGE_CONFIG[channelType] : null
                        return (
                        <div
                            key={c.id}
                            className='d-flex flex-stack py-1 cursor-pointer rounded px-2 conversation-item'
                            style={selectedId === c.id
                                ? { backgroundColor: 'rgba(62, 151, 255, 0.18)', borderBottom: '1px solid transparent' }
                                : { borderBottom: '1px solid #f0f0f5' }
                            }
                            onClick={() => onSelect(c)}
                        >
                            {/* Avatar */}
                            <div className='d-flex align-items-center' style={{ minWidth: 0 }}>
                                <div className='symbol symbol-35px symbol-circle flex-shrink-0'>
                                    {c.avatar ? (
                                        <img alt={intl.formatMessage(C.avatarAlt)} src={c.avatar} />
                                    ) : (
                                        <span className='symbol-label bg-light-danger text-danger fs-7 fw-bolder'>
                                            {c.full_name
                                                ? c.full_name.trim().split(/\s+/).filter(Boolean).map(w => w[0]).filter((_, i, arr) => i === 0 || i === arr.length - 1).join('').toUpperCase()
                                                : UNKNOWN_INITIAL}
                                        </span>
                                    )}
                                </div>

                                <div className='ms-3' style={{ minWidth: 0 }}>
                                    <div className='d-flex align-items-center gap-1'>
                                        {channelConfig && (
                                            <i
                                                className={`bi ${channelConfig.icon}`}
                                                style={{ fontSize: '0.75rem', color: channelConfig.backgroundColor, flexShrink: 0 }}
                                                title={channelConfig.label}
                                            ></i>
                                        )}
                                        <span className='fs-6 fw-bold text-gray-900 text-truncate' style={{ lineHeight: '1.3' }}>
                                            {c.full_name}
                                        </span>
                                    </div>
                                    <div className='fs-8 text-gray-700 contact-phone-number'>
                                        {c.phone ? formatPhone(c.phone) : ''}
                                    </div>
                                    <div className='fs-9 text-gray-600 text-truncate' style={{ maxWidth: '155px' }}>
                                        {c.last_message ? (
                                            <>
                                                {c.last_message.direction === 'OUTBOUND' && <i className='bi bi-check2-all me-1 text-gray-600'></i>}
                                                {c.last_message.content}
                                            </>
                                        ) : (
                                            <span className='fst-italic'>{intl.formatMessage(C.noMessages)}</span>
                                        )}
                                    </div>

                                    {c.tags && c.tags.length > 0 && (
                                        <div className='d-flex flex-wrap gap-1 mt-1'>
                                            {c.tags.map(tag => (
                                                <span
                                                    key={tag.id}
                                                    className='badge badge-sm px-1 py-0'
                                                    style={{
                                                        backgroundColor: tag.color + '20',
                                                        color: tag.color,
                                                        border: `1px solid ${tag.color}`,
                                                        fontSize: '0.6rem'
                                                    }}
                                                >
                                                    {tag.name}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Right column: date, unread, assigned user */}
                            <div className='d-flex flex-column align-items-end ms-2 flex-shrink-0'>
                                {/* Channel badges */}
                                <div className="d-flex gap-1 mb-1">
                                    {(() => {
                                        if (c.last_message) {
                                            const badgeInfo = getChannelBadgeInfo(c.last_message as any)
                                            if (badgeInfo) {
                                                return (
                                                    <span
                                                        className="badge d-flex align-items-center px-2"
                                                        title={getChannelBadgeTooltip(c.last_message as any)}
                                                        style={{
                                                            height: '18px',
                                                            fontSize: '0.6rem',
                                                            backgroundColor: badgeInfo.backgroundColor,
                                                            color: badgeInfo.color,
                                                            fontWeight: 600,
                                                            borderRadius: '4px',
                                                            flexShrink: 0,
                                                            cursor: 'help'
                                                        }}
                                                    >
                                                        <i className={`bi ${badgeInfo.icon} me-1`} style={{ fontSize: '0.6rem' }}></i>
                                                        {badgeInfo.shortPhone}
                                                    </span>
                                                )
                                            }
                                        }

                                        if (c.active_channels_detail && c.active_channels_detail.length > 0) {
                                            return (
                                                <>
                                                    {c.active_channels_detail.filter(d => d && d.channel_type).map((detail, idx) => {
                                                        const isWhatsApp = detail.channel_type === 'WHATSAPP'
                                                        const isSms = detail.channel_type === 'SMS'
                                                        const phoneDisplay = detail.phone_number ? formatPhone(detail.phone_number) : detail.channel_type

                                                        return (
                                                            <span
                                                                key={idx}
                                                                className="badge d-flex align-items-center px-2"
                                                                title={channelTitle(detail.channel_type, detail.phone_number)}
                                                                style={{
                                                                    backgroundColor: isWhatsApp ? '#25d366' : isSms ? '#3e97ff' : '#6c757d',
                                                                    color: '#fff',
                                                                    fontSize: '0.6rem',
                                                                    fontWeight: 600,
                                                                    flexShrink: 0,
                                                                    height: '18px',
                                                                    borderRadius: '4px',
                                                                    gap: '3px',
                                                                }}
                                                            >
                                                                {isWhatsApp ? (
                                                                    <>
                                                                        <i className="bi bi-whatsapp" style={{ fontSize: '0.8rem' }}></i>
                                                                        {CHANNEL_WA_SHORT}
                                                                    </>
                                                                ) : isSms ? (
                                                                    CHANNEL_SMS
                                                                ) : (
                                                                    phoneDisplay
                                                                )}
                                                            </span>
                                                        )
                                                    })}
                                                </>
                                            )
                                        } else if (c.active_channels) {
                                            return (
                                                <>
                                                    {c.active_channels.includes('WHATSAPP') && (
                                                        <span
                                                            className="badge d-flex align-items-center px-2"
                                                            title={CHANNEL_WHATSAPP}
                                                            style={{ backgroundColor: '#25d366', color: '#fff', fontSize: '0.6rem', fontWeight: 600, height: '18px', borderRadius: '4px', flexShrink: 0, gap: '3px' }}
                                                        >
                                                            <i className="bi bi-whatsapp" style={{ fontSize: '0.8rem' }}></i>
                                                            {CHANNEL_WA_SHORT}
                                                        </span>
                                                    )}
                                                    {c.active_channels.includes('SMS') && (
                                                        <span
                                                            className="badge d-flex align-items-center px-1"
                                                            title={CHANNEL_SMS}
                                                            style={{
                                                                height: '18px',
                                                                fontSize: '0.6rem',
                                                                backgroundColor: '#3e97ff',
                                                                color: '#fff',
                                                                fontWeight: 600,
                                                                borderRadius: '4px',
                                                                flexShrink: 0
                                                            }}
                                                        >
                                                            {CHANNEL_SMS}
                                                        </span>
                                                    )}
                                                </>
                                            )
                                        }

                                        if (c.last_message?.channel === 'WHATSAPP') {
                                            return (
                                                <span
                                                    className="badge d-flex align-items-center px-2"
                                                    style={{ backgroundColor: '#25d366', color: '#fff', fontSize: '0.6rem', fontWeight: 600, height: '18px', borderRadius: '4px', flexShrink: 0, gap: '3px' }}
                                                >
                                                    <i className="bi bi-whatsapp" style={{ fontSize: '0.8rem' }}></i>
                                                    {CHANNEL_WA_SHORT}
                                                </span>
                                            )
                                        }

                                        return null
                                    })()}
                                </div>

                                {/* Date */}
                                <span className='text-gray-700 mb-1 fs-8' style={{ whiteSpace: 'nowrap' }}>
                                    {c.last_message?.created_at
                                        ? formatConversationDate(c.last_message.created_at)
                                        : ''}
                                </span>

                                {/* Unread count */}
                                {c.unread_count > 0 && (
                                    <span className='badge badge-sm badge-circle badge-light-danger'>
                                        {c.unread_count}
                                    </span>
                                )}

                                {/* Assigned user badge — only in General/All mode */}
                                {(inboxMode !== 'my' || isSupervisor) && c.assigned_to_name && (
                                    <span
                                        className='badge mt-1 text-truncate'
                                        style={{
                                            maxWidth: '100px',
                                            fontSize: '0.6rem',
                                            backgroundColor: '#e8edf5',
                                            color: '#3f4254',
                                            border: '1px solid #b5bbd6',
                                            fontWeight: 600,
                                            borderRadius: '4px',
                                            padding: '2px 6px',
                                        }}
                                        title={c.assigned_to_name}
                                    >
                                        {(() => {
                                            const parts = c.assigned_to_name.trim().split(/\s+/)
                                            const name = parts.length >= 2 ? `${parts[0]} ${parts[parts.length - 1]}` : parts[0]
                                            return name.toUpperCase()
                                        })()}
                                    </span>
                                )}
                            </div>
                        </div>
                        )
                    })}

                    <div className='separator my-1'></div>
                </div>
            </div>
        </div >
    )
}

export { ConversationList }
